/**
 * The per-cell audit view.
 *
 * Collapsed by default, since the table above is the same content in a
 * friendlier shape. Expanded, this is where a scale or a sign bug becomes
 * visible: `value_raw` → `value_western` → `value_printed` → `value`, plus
 * the scale, the sign, and the nil and parens flags (§10.4).
 *
 * The four columns are the pipeline's four steps, in order, so a wrong one
 * names the step that produced it.
 */
import type { PageFact } from '@server/sql/page.ts'
import { Badge, NotRecorded } from '~/components/ui/primitives.tsx'
import { Raw, RawNumber, Untranslated } from '~/components/ui/raw.tsx'
import { Td, Th, Tr } from '~/components/ui/table.tsx'
import { useT } from '~/i18n/index.tsx'
import { date, money } from '~/i18n/format.ts'
import { cn } from '~/lib/cn.ts'

export function FactAudit({ facts }: { facts: PageFact[] }) {
  const { t, locale, v } = useT()

  return (
    <div className="overflow-x-auto rounded-[var(--radius-container)] border border-[var(--border)]">
      <table className="w-full border-collapse text-[length:var(--text-tiny)]">
        <thead>
          <Tr>
            <Th>{t('fact.label')}</Th>
            <Th>{t('fact.column')}</Th>
            <Th>{t('fact.period')}</Th>
            <Th numeric>{t('fact.raw')}</Th>
            <Th numeric>{t('fact.western')}</Th>
            <Th numeric>{t('fact.printed')}</Th>
            <Th numeric>{t('fact.value')}</Th>
            <Th numeric>{t('fact.scale')}</Th>
            <Th>{t('viewer.flags')}</Th>
          </Tr>
        </thead>
        <tbody>
          {facts.map((f) => {
            // The one comparison this table exists to make cheap: the typed
            // value should be the printed one times the scale. When it is
            // not, the scale step is where to look.
            const drift =
              f.valuePrinted !== null && f.value !== null && f.scaleApplied !== null &&
              Number(f.valuePrinted) * f.scaleApplied !== Number(f.value)

            return (
              <Tr key={f.id} className={cn(f.isTotal && 'bg-[var(--bg-sunken)]/60')}>
                <Td>
                  <span style={{ paddingInlineStart: (f.level ?? 0) * 10 }} className="inline-block">
                    <Raw lang={f.labelLang} className={cn(f.isTotal && 'font-semibold')}>
                      {f.labelRaw}
                    </Raw>
                    {f.noteRef && (
                      <sup className="ms-1 text-[var(--text-faint)]">
                        <Raw lang={f.labelLang}>{f.noteRef}</Raw>
                      </sup>
                    )}
                  </span>
                  {/* Where `concept` is non-null it is shown -- so seeding the
                      vocabulary later improves this view with no code change. */}
                  {f.concept && (
                    <div className="mt-0.5">
                      <Untranslated>{f.concept}</Untranslated>
                    </div>
                  )}
                </Td>
                <Td className="max-w-[10rem]">
                  <Raw lang={f.labelLang} className="block truncate text-[var(--text-muted)]">
                    {f.columnLabel ?? ''}
                  </Raw>
                </Td>
                <Td className="whitespace-nowrap text-[var(--text-muted)]">
                  {f.periodEnd ? date(f.periodEnd, locale) : <NotRecorded>—</NotRecorded>}
                  {f.periodType && (
                    <span className="ms-1 text-[var(--text-faint)]">{f.periodType}</span>
                  )}
                </Td>
                <Td numeric><RawNumber value={f.valueRaw} lang={f.labelLang} /></Td>
                <Td numeric><RawNumber value={f.valueWestern} /></Td>
                <Td numeric className="text-[var(--text-muted)]">
                  {f.valuePrinted === null ? '—' : money(f.valuePrinted, locale)}
                </Td>
                <Td numeric className={cn(drift && 'text-[var(--fail)]')}>
                  {f.value === null ? '—' : money(f.value, locale)}
                </Td>
                <Td numeric className="text-[var(--text-faint)]">
                  {f.scaleApplied === null ? '—' : `×${f.scaleApplied}`}
                </Td>
                <Td>
                  <span className="flex flex-wrap gap-1">
                    {f.fromParens && <Badge state="warn">{t('fact.parens')}</Badge>}
                    {f.isNil && <Badge state="neutral">{t('fact.nil')}</Badge>}
                    {f.isTotal && <Badge state="neutral">{t('fact.total')}</Badge>}
                    {f.columnAssurance && (
                      <Badge state="neutral">
                        {v('assurance', f.columnAssurance) ?? f.columnAssurance}
                      </Badge>
                    )}
                  </span>
                </Td>
              </Tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
